import { DatePipe } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CachedBuildingService, UserService } from '@cfp/core';
import { AlertService } from '@cfp/ui/feedback';
import { BuildingDetailsDTO } from 'csr-asset-app';
import { toJpeg, toPng } from 'html-to-image';
import { firstValueFrom, map } from 'rxjs';

const EXPORT_CHROME_SELECTOR = [
  '.d-print-none',
  '.roadmap-table-add-row',
  'cfp-measure-status-action-dropdown',
  'cfp-sort-icon',
  '.btn.btn-sm.btn-light',
  '.td-content > .btn',
  '.td-content > .fa'
].join(', ');

interface ExportViewport {
  readonly width: number;
  readonly height: number;
  readonly scrollLeft: number;
  readonly scrollTop: number;
}

@Injectable()
export class RoadmapTableImageExportService {
  private readonly userService = inject(UserService);
  private readonly cachedBuildingService = inject(CachedBuildingService);
  private readonly datePipe = inject(DatePipe);
  private readonly alertService = inject(AlertService);

  private readonly customerName = toSignal(this.userService.get().pipe(map((user) => user?.primaryCustomer?.name)));

  public async exportToPng(tableContainer: HTMLElement, buildingId?: number): Promise<void> {
    await this.export(tableContainer, 'image/png', buildingId);
  }

  public async exportToJpg(tableContainer: HTMLElement, buildingId?: number): Promise<void> {
    await this.export(tableContainer, 'image/jpeg', buildingId);
  }

  private async export(
    tableContainer: HTMLElement,
    type: 'image/png' | 'image/jpeg',
    buildingId?: number
  ): Promise<void> {
    try {
      const viewport = this.resolveExportViewport(tableContainer);

      if (viewport.width <= 0 || viewport.height <= 0) {
        throw new Error('Table viewport has no size');
      }

      await document.fonts.ready;

      const dataUrl = await this.rasterize(tableContainer, type, viewport);
      const fileName = await this.createFileName(type, buildingId);
      this.downloadDataUrl(dataUrl, fileName);
    } catch {
      this.alertService.showAlert('error', 'error.general');
    }
  }

  private resolveExportViewport(tableContainer: HTMLElement): ExportViewport {
    const scrollTop = tableContainer.scrollTop;
    const clientWidth = tableContainer.clientWidth;
    const clientHeight = tableContainer.clientHeight;
    const table = tableContainer.querySelector('table');

    if (!table) {
      return {
        width: clientWidth,
        height: clientHeight,
        scrollLeft: tableContainer.scrollLeft,
        scrollTop
      };
    }

    const columns = this.getColumnBounds(table);
    // Align so the first year after the sticky Measure column starts on a column boundary
    // (avoids a clipped start year like "26" instead of "2026").
    const scrollLeft = this.snapScrollLeftToColumnStart(columns, tableContainer.scrollLeft);
    const width = this.snapWidthToFullRightColumn(table, columns, scrollLeft, clientWidth);
    const addRow = table.querySelector('.roadmap-table-add-row') as HTMLElement | null;
    const addRowHeight = addRow?.offsetHeight ?? 0;
    const contentHeight = Math.max(table.offsetHeight - addRowHeight - scrollTop, 0);
    const height = contentHeight > 0 ? Math.min(clientHeight, contentHeight) : clientHeight;

    return { width, height, scrollLeft, scrollTop };
  }

  private getColumnBounds(table: HTMLTableElement): ReadonlyArray<{ left: number; right: number; width: number }> {
    const headerRow = table.querySelector('thead tr') ?? table.querySelector('tr');
    if (!headerRow) {
      return [];
    }

    const cells = Array.from(headerRow.children) as HTMLElement[];
    let offset = 0;
    return cells.map((cell) => {
      const width = cell.offsetWidth;
      const left = offset;
      const right = offset + width;
      offset = right;
      return { left, right, width };
    });
  }

  /**
   * With a sticky first column, the first visible year pixel is at scrollLeft + firstColWidth.
   * Snap scrollLeft back so that pixel lands on a year column's left edge.
   */
  private snapScrollLeftToColumnStart(
    columns: ReadonlyArray<{ left: number; right: number; width: number }>,
    scrollLeft: number
  ): number {
    if (columns.length < 2 || scrollLeft <= 0) {
      return Math.max(scrollLeft, 0);
    }

    const firstColWidth = columns[0].width;
    const contentStart = scrollLeft + firstColWidth;

    for (let index = 1; index < columns.length; index++) {
      const column = columns[index];
      if (contentStart <= column.left + 0.5) {
        return Math.max(column.left - firstColWidth, 0);
      }
      if (contentStart < column.right - 0.5) {
        // Partially through this year column → show it from the start.
        return Math.max(column.left - firstColWidth, 0);
      }
    }

    return scrollLeft;
  }

  /**
   * Expand the capture width so a partially visible rightmost year column is fully included.
   */
  private snapWidthToFullRightColumn(
    table: HTMLTableElement,
    columns: ReadonlyArray<{ left: number; right: number; width: number }>,
    scrollLeft: number,
    clientWidth: number
  ): number {
    if (columns.length === 0) {
      return clientWidth;
    }

    const viewportRight = scrollLeft + clientWidth;
    let snappedRight = viewportRight;

    for (const column of columns) {
      // Column starts in view (or before) and extends past the right edge → include it fully.
      if (column.left < viewportRight && column.right > viewportRight) {
        snappedRight = column.right;
        break;
      }
    }

    const maxWidth = Math.max(table.offsetWidth - scrollLeft, clientWidth);
    return Math.min(Math.max(snappedRight - scrollLeft, clientWidth), maxWidth);
  }

  private async rasterize(
    tableContainer: HTMLElement,
    type: 'image/png' | 'image/jpeg',
    viewport: ExportViewport
  ): Promise<string> {
    const { host, root } = this.createPreparedExportRoot(tableContainer, viewport);

    try {
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

      // Re-measure after chrome removal so leftover add-row space is not exported.
      const table = root.querySelector('table');
      const fittedHeight =
        table != null
          ? Math.min(viewport.height, Math.max(table.offsetHeight - viewport.scrollTop, 0))
          : viewport.height;
      const height = fittedHeight > 0 ? fittedHeight : viewport.height;
      const width = viewport.width;

      root.style.width = `${width}px`;
      root.style.height = `${height}px`;

      const options = {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: true,
        width,
        height,
        filter: (node: HTMLElement) => this.shouldIncludeInExport(node),
        style: {
          width: `${width}px`,
          height: `${height}px`,
          overflow: 'hidden',
          margin: '0'
        }
      };

      if (type === 'image/png') {
        return await toPng(root, options);
      }

      return await toJpeg(root, { ...options, quality: 1 });
    } finally {
      host.remove();
    }
  }

  private createPreparedExportRoot(
    tableContainer: HTMLElement,
    viewport: ExportViewport
  ): { host: HTMLElement; root: HTMLElement } {
    const host = document.createElement('div');
    host.className = 'roadmap-table-image-export-host';

    const root = tableContainer.cloneNode(true) as HTMLElement;
    root.classList.add('roadmap-table-image-export-root');
    root.style.width = `${viewport.width}px`;
    root.style.height = `${viewport.height}px`;
    root.style.overflow = 'hidden';
    root.style.margin = '0';
    root.style.position = 'relative';

    this.stripExportChrome(root);
    this.applyViewportScroll(root, viewport.scrollLeft, viewport.scrollTop);

    host.appendChild(root);
    document.body.appendChild(host);
    return { host, root };
  }

  private stripExportChrome(root: HTMLElement): void {
    root.querySelectorAll(EXPORT_CHROME_SELECTOR).forEach((element) => element.remove());
  }

  private applyViewportScroll(root: HTMLElement, scrollLeft: number, scrollTop: number): void {
    const table = root.querySelector('table');
    if (!table) {
      return;
    }

    table.classList.remove('table-header-sticky');
    // html-to-image does not preserve scrollLeft; shift the table to match the live viewport.
    table.style.transform = `translate(${-scrollLeft}px, ${-scrollTop}px)`;

    // Keep the sticky first column pinned to the left edge of the viewport.
    table.querySelectorAll('tr > :first-child').forEach((cell) => {
      const htmlCell = cell as HTMLElement;
      htmlCell.style.position = 'relative';
      htmlCell.style.left = 'auto';
      htmlCell.style.transform = `translateX(${scrollLeft}px)`;
      htmlCell.style.zIndex = '3';
      if (!htmlCell.style.backgroundColor) {
        htmlCell.style.backgroundColor = '#ffffff';
      }
    });
  }

  private shouldIncludeInExport(node: HTMLElement): boolean {
    if (!(node instanceof Element) || !node.classList) {
      return true;
    }

    return !node.classList.contains('d-print-none') && !node.classList.contains('roadmap-table-add-row');
  }

  private async createFileName(type: 'image/png' | 'image/jpeg', buildingId?: number): Promise<string> {
    const datePrefix = this.datePipe.transform(new Date(), 'yyyyMMdd') ?? '';
    let context = this.customerName() ?? '';

    if (buildingId) {
      try {
        const building = await firstValueFrom(this.cachedBuildingService.v2BuildingsIdGet(buildingId));
        const address = (building as BuildingDetailsDTO).address?.display?.join('-') ?? '';
        context = `${context}_${address}`;
      } catch {
        // Keep customer-only context when building lookup fails.
      }
    }

    const sanitizedContext = context.replace(/ /g, '-');
    const extension = type === 'image/png' ? 'png' : 'jpg';
    return `${datePrefix}${sanitizedContext ? `_${sanitizedContext}` : ''}_roadmap.${extension}`;
  }

  private downloadDataUrl(dataUrl: string, fileName: string): void {
    const link = document.createElement('a');
    link.download = fileName;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
